import type { QuadCorners } from "./operations";

interface Pixels { data: Uint8ClampedArray; width: number; height: number }

/** Otsu's threshold for 0-255 grey values. */
function otsu(gray: Uint8Array): number {
  const hist = new Array(256).fill(0);
  for (let i = 0; i < gray.length; i++) hist[gray[i]]++;
  const total = gray.length;
  let sum = 0;
  for (let i = 0; i < 256; i++) sum += i * hist[i];
  let sumB = 0, wB = 0, best = 0, thr = 128;
  for (let t = 0; t < 256; t++) {
    wB += hist[t];
    if (!wB) continue;
    const wF = total - wB;
    if (!wF) break;
    sumB += t * hist[t];
    const mB = sumB / wB, mF = (sum - sumB) / wF;
    const between = wB * wF * (mB - mF) ** 2;
    if (between > best) { best = between; thr = t; }
  }
  return thr;
}

function boxBlur(src: Uint8Array, w: number, h: number, r: number): Uint8Array {
  const tmp = new Float32Array(w * h), out = new Uint8Array(w * h);
  for (let y = 0; y < h; y++) {
    let acc = 0, n = 0;
    for (let x = -r; x < w; x++) {
      const add = x + r, rem = x - r - 1;
      if (add < w) { acc += src[y * w + add]; n++; }
      if (rem >= 0) { acc -= src[y * w + rem]; n--; }
      if (x >= 0) tmp[y * w + x] = acc / n;
    }
  }
  for (let x = 0; x < w; x++) {
    let acc = 0, n = 0;
    for (let y = -r; y < h; y++) {
      const add = y + r, rem = y - r - 1;
      if (add < h) { acc += tmp[add * w + x]; n++; }
      if (rem >= 0) { acc -= tmp[rem * w + x]; n--; }
      if (y >= 0) out[y * w + x] = acc / n;
    }
  }
  return out;
}

/**
 * Finds the page in a photo: the largest bright region (paper is usually lighter than the table behind it),
 * and its four extreme corners. Works on a small copy for speed. Returns corners in the input's pixel
 * coordinates, or null when no page-like region is found (so the caller can leave the corners to the person).
 */
export function detectDocumentQuad(img: Pixels, maxSide = 320): QuadCorners | null {
  const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
  const w = Math.max(8, Math.round(img.width * scale)), h = Math.max(8, Math.round(img.height * scale));
  const gray = new Uint8Array(w * h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const sx = Math.min(img.width - 1, Math.floor(x / scale)), sy = Math.min(img.height - 1, Math.floor(y / scale));
      const i = (sy * img.width + sx) * 4;
      gray[y * w + x] = 0.299 * img.data[i] + 0.587 * img.data[i + 1] + 0.114 * img.data[i + 2];
    }
  }
  const blurred = boxBlur(gray, w, h, Math.max(2, Math.round(Math.min(w, h) / 60)));
  const thr = otsu(blurred);
  const mask = new Uint8Array(w * h);
  for (let i = 0; i < mask.length; i++) mask[i] = blurred[i] > thr ? 1 : 0;

  // Largest 4-connected component of the bright mask.
  const label = new Int32Array(w * h);
  let bestId = 0, bestSize = 0, id = 0;
  const stack: number[] = [];
  for (let s = 0; s < mask.length; s++) {
    if (!mask[s] || label[s]) continue;
    id++;
    let size = 0;
    stack.push(s); label[s] = id;
    while (stack.length) {
      const p = stack.pop()!;
      size++;
      const x = p % w, y = (p - x) / w;
      if (x > 0 && mask[p - 1] && !label[p - 1]) { label[p - 1] = id; stack.push(p - 1); }
      if (x < w - 1 && mask[p + 1] && !label[p + 1]) { label[p + 1] = id; stack.push(p + 1); }
      if (y > 0 && mask[p - w] && !label[p - w]) { label[p - w] = id; stack.push(p - w); }
      if (y < h - 1 && mask[p + w] && !label[p + w]) { label[p + w] = id; stack.push(p + w); }
    }
    if (size > bestSize) { bestSize = size; bestId = id; }
  }
  const frac = bestSize / (w * h);
  if (frac < 0.15 || frac > 0.97) return null;

  let tl = Infinity, br = -Infinity, tr = -Infinity, bl = Infinity;
  let TL = [0, 0], BR = [0, 0], TR = [0, 0], BL = [0, 0];
  for (let p = 0; p < label.length; p++) {
    if (label[p] !== bestId) continue;
    const x = p % w, y = (p - x) / w;
    const a = x + y, b = x - y;
    if (a < tl) { tl = a; TL = [x, y]; }
    if (a > br) { br = a; BR = [x, y]; }
    if (b > tr) { tr = b; TR = [x, y]; }
    if (b < bl) { bl = b; BL = [x, y]; }
  }
  const up = (p: number[]) => ({ x: Math.min(img.width, (p[0] + 0.5) / scale), y: Math.min(img.height, (p[1] + 0.5) / scale) });
  return { topLeft: up(TL), topRight: up(TR), bottomRight: up(BR), bottomLeft: up(BL) };
}

/**
 * Evens out uneven lighting and shadows: estimates the paper colour block by block (the lightest pixels,
 * which are paper, not ink), smooths it, and divides it out so the page background becomes white.
 */
export function flattenIllumination(img: Pixels, block = 24): boolean {
  const { data, width: w, height: h } = img;
  // A page with no real contrast (a plain or flat coloured sheet) has no shadows to even out; stretching it only
  // pushes a dark channel towards black (B-0174). Leave it as it is.
  if (analyzeTone(data).lowContrast) return false;
  const gw = Math.ceil(w / block), gh = Math.ceil(h / block);
  const bg = [0, 1, 2].map(() => new Uint8Array(gw * gh));
  for (let gy = 0; gy < gh; gy++) {
    for (let gx = 0; gx < gw; gx++) {
      for (let c = 0; c < 3; c++) {
        const vals: number[] = [];
        for (let y = gy * block; y < Math.min(h, (gy + 1) * block); y += 2) {
          for (let x = gx * block; x < Math.min(w, (gx + 1) * block); x += 2) vals.push(data[(y * w + x) * 4 + c]);
        }
        vals.sort((a, b) => a - b);
        bg[c][gy * gw + gx] = vals[Math.floor(vals.length * 0.92)] ?? 255; // near the top: paper, not ink
      }
    }
  }
  const smooth = bg.map(g => boxBlur(g, gw, gh, 1));
  for (let y = 0; y < h; y++) {
    const fy = Math.min(gh - 1, Math.max(0, y / block - 0.5)), y0 = Math.floor(fy), y1 = Math.min(gh - 1, y0 + 1), ty = fy - y0;
    for (let x = 0; x < w; x++) {
      const fx = Math.min(gw - 1, Math.max(0, x / block - 0.5)), x0 = Math.floor(fx), x1 = Math.min(gw - 1, x0 + 1), tx = fx - x0;
      const i = (y * w + x) * 4;
      for (let c = 0; c < 3; c++) {
        const g = smooth[c];
        const b = (g[y0 * gw + x0] * (1 - tx) + g[y0 * gw + x1] * tx) * (1 - ty) + (g[y1 * gw + x0] * (1 - tx) + g[y1 * gw + x1] * tx) * ty;
        data[i + c] = Math.min(255, (data[i + c] * 255) / Math.max(MIN_PAPER_LEVEL, b)); // gain capped at 255 / 64, about 4x
      }
    }
  }
  return true;
}

/** Lowest paper level that shadow removal will brighten to white; a channel below this is amplified at most about 4x. */
export const MIN_PAPER_LEVEL = 64;
/** Luminance spread (99.5th minus 0.5th percentile) under which a page counts as having no real contrast. */
export const LOW_CONTRAST_SPREAD = 24;
/** Paper brightness the scan aims for when the page itself is dark or flat, so the result stays readable. */
export const READABLE_PAPER_TARGET = 170;

export interface ToneInfo {
  /** Typical page brightness, 0-255 (90th percentile of luminance: paper, not ink). */
  paper: number;
  /** How far apart the darkest and lightest content are (99.5th minus 0.5th percentile). */
  spread: number;
  lowContrast: boolean;
  darkPage: boolean;
}

/** Reads how bright and how contrasty a page is, from RGBA pixels. */
export function analyzeTone(data: Uint8ClampedArray): ToneInfo {
  const hist = new Array<number>(256).fill(0);
  let n = 0;
  for (let i = 0; i < data.length; i += 4) { hist[Math.round(0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2])]++; n++; }
  const pct = (q: number) => { let acc = 0; for (let v = 0; v < 256; v++) { acc += hist[v]; if (acc >= q * n) return v; } return 255; };
  const paper = pct(0.9), spread = pct(0.995) - pct(0.005);
  return { paper, spread, lowContrast: spread < LOW_CONTRAST_SPREAD, darkPage: paper < 128 };
}

const clamp255 = (v: number) => (v < 0 ? 0 : v > 255 ? 255 : v);

/**
 * The scan's tone step (grayscale and contrast). Normal photos, with a light page and real contrast, get the same
 * contrast curve as before. A dark or flat page is never stretched towards black: the page is lifted to a readable
 * grey and any real ink keeps its contrast around it; a page with no contrast is not stretched at all.
 */
export function applyScanTone(data: Uint8ClampedArray, contrast: number, grayscale: boolean): ToneInfo {
  const info = analyzeTone(data);
  const f = (259 * (contrast + 255)) / (255 * (259 - contrast));
  const normal = !info.lowContrast && !info.darkPage;
  const gain = info.lowContrast ? 1 : f;
  const lift = Math.min(2.5, READABLE_PAPER_TARGET / Math.max(1, info.paper));
  for (let i = 0; i < data.length; i += 4) {
    if (grayscale) {
      const g = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      const v = normal ? f * (g - 128) + 128 : gain * (g - info.paper) + Math.max(info.paper, READABLE_PAPER_TARGET);
      data[i] = data[i + 1] = data[i + 2] = clamp255(v);
    } else if (normal) {
      data[i] = clamp255(f * (data[i] - 128) + 128);
      data[i + 1] = clamp255(f * (data[i + 1] - 128) + 128);
      data[i + 2] = clamp255(f * (data[i + 2] - 128) + 128);
    } else if (info.paper < READABLE_PAPER_TARGET) {
      // Colour kept: brighten every channel by the same factor so the hue stays.
      data[i] = clamp255(data[i] * lift); data[i + 1] = clamp255(data[i + 1] * lift); data[i + 2] = clamp255(data[i + 2] * lift);
    }
  }
  return info;
}

export const PDF_PAGE_SIZES = { a4: [595.28, 841.89], letter: [612, 792] } as const;
export type ScanPageSize = "a4" | "letter" | "fit";

/** Size of the PDF page for an image: A4/Letter in the image's orientation (with a small margin), or the image at 150 dpi. */
export function pdfPageFor(imgW: number, imgH: number, size: ScanPageSize): { pageW: number; pageH: number; drawW: number; drawH: number } {
  if (size === "fit") { const k = 72 / 150; return { pageW: imgW * k, pageH: imgH * k, drawW: imgW * k, drawH: imgH * k }; }
  const [pw, ph] = PDF_PAGE_SIZES[size];
  const landscape = pageOrientationFor(imgW, imgH) === "landscape";
  const pageW = landscape ? ph : pw, pageH = landscape ? pw : ph;
  const margin = 18;
  const s = Math.min((pageW - margin * 2) / imgW, (pageH - margin * 2) / imgH);
  return { pageW, pageH, drawW: imgW * s, drawH: imgH * s };
}

/** Reads the EXIF Orientation tag (1-8) from the start of a JPEG file. Returns 1 (upright) when absent or not a JPEG. */
export function readExifOrientation(buf: ArrayBuffer): number {
  const v = new DataView(buf);
  if (v.byteLength < 4 || v.getUint16(0) !== 0xffd8) return 1;
  let off = 2;
  while (off + 4 <= v.byteLength) {
    const marker = v.getUint16(off);
    if (marker === 0xffe1) {
      const start = off + 4;
      if (start + 10 > v.byteLength || v.getUint32(start) !== 0x45786966) return 1; // "Exif"
      const tiff = start + 6;
      const little = v.getUint16(tiff) === 0x4949;
      const ifd = tiff + v.getUint32(tiff + 4, little);
      if (ifd + 2 > v.byteLength) return 1;
      const n = v.getUint16(ifd, little);
      for (let i = 0; i < n; i++) {
        const e = ifd + 2 + i * 12;
        if (e + 10 > v.byteLength) return 1;
        if (v.getUint16(e, little) === 0x0112) {
          const o = v.getUint16(e + 8, little);
          return o >= 1 && o <= 8 ? o : 1;
        }
      }
      return 1;
    }
    if ((marker & 0xff00) !== 0xff00 || marker === 0xffda) return 1;
    off += 2 + v.getUint16(off + 2);
  }
  return 1;
}

/** Canvas transform [a,b,c,d,e,f] that draws an unrotated w x h image upright for an EXIF orientation, plus the output size. */
export function exifTransform(orientation: number, w: number, h: number): { m: [number, number, number, number, number, number]; width: number; height: number } {
  switch (orientation) {
    case 2: return { m: [-1, 0, 0, 1, w, 0], width: w, height: h };
    case 3: return { m: [-1, 0, 0, -1, w, h], width: w, height: h };
    case 4: return { m: [1, 0, 0, -1, 0, h], width: w, height: h };
    case 5: return { m: [0, 1, 1, 0, 0, 0], width: h, height: w };
    case 6: return { m: [0, 1, -1, 0, h, 0], width: h, height: w };
    case 7: return { m: [0, -1, -1, 0, h, w], width: h, height: w };
    case 8: return { m: [0, -1, 1, 0, 0, w], width: h, height: w };
    default: return { m: [1, 0, 0, 1, 0, 0], width: w, height: h };
  }
}

export const MIN_SCAN_SHORT_SIDE = 600;
/** True when the photo is too small to give a readable scan (short side under about 600 px). */
export function isLowResolution(w: number, h: number): boolean {
  return Math.min(w, h) < MIN_SCAN_SHORT_SIDE;
}

/** US Letter for US/Canada English locales, A4 everywhere else. */
export function defaultPageSizeForLocale(locale: string | undefined | null): "a4" | "letter" {
  const l = (locale || "").replace("_", "-").toLowerCase();
  return l === "en-us" || l === "en-ca" ? "letter" : "a4";
}

/** Page orientation chosen from the corrected image (after EXIF fix, straightening and rotation). */
export function pageOrientationFor(imgW: number, imgH: number): "portrait" | "landscape" {
  return imgW > imgH ? "landscape" : "portrait";
}

/** True when the cleaned scan is, for practical purposes, a black page (average brightness under 30 of 255). */
export function isNearlyBlack(data: Uint8ClampedArray): boolean {
  let sum = 0, n = 0;
  for (let i = 0; i < data.length; i += 4) { sum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]; n++; }
  return n > 0 && sum / n < 30;
}

/** A copy of the list with the item at `index` moved by `delta` places (clamped); the same list when nothing moves. */
export function movePage<T>(list: readonly T[], index: number, delta: number): T[] {
  const to = Math.max(0, Math.min(list.length - 1, index + delta));
  if (index < 0 || index >= list.length || to === index) return list.slice();
  const out = list.slice();
  const [item] = out.splice(index, 1);
  out.splice(to, 0, item);
  return out;
}
